import { themesApi, type ProjectAppliedTheme } from "./themes";

/**
 * Module-level cache of GET /projects/:id/theme per project (#54).
 *
 * Every theme chat card in a reopened session needs the server's applied-theme
 * snapshot to derive its state; without dedup, N historical cards would fire N
 * identical requests on mount. Failed fetches are NOT cached so a transient
 * error doesn't poison every later card.
 *
 * Subscribers exist because the applied theme is SHARED state: when one card
 * (or the Themes panel) applies a theme, every other mounted card's derived
 * state changes too — the previously-applied card must stop claiming "Applied
 * to project" and offer Re-apply instead. Without this, that only corrected
 * itself on reload.
 */
const cache = new Map<string, Promise<ProjectAppliedTheme | null>>();
const subscribers = new Map<string, Set<() => void>>();

export function getProjectThemeCached(
  token: string,
  projectId: string,
): Promise<ProjectAppliedTheme | null> {
  const existing = cache.get(projectId);
  if (existing) return existing;

  const promise = themesApi.getProjectTheme(token, projectId).catch((err) => {
    cache.delete(projectId);
    throw err;
  });
  cache.set(projectId, promise);
  return promise;
}

/**
 * Drop the cached snapshot and tell every mounted consumer to re-read it.
 * Call after every successful apply/remove.
 */
export function invalidateProjectTheme(projectId: string): void {
  cache.delete(projectId);
  subscribers.get(projectId)?.forEach((notify) => notify());
}

/** Subscribe to invalidations for a project. Returns an unsubscribe fn. */
export function subscribeProjectTheme(
  projectId: string,
  onInvalidated: () => void,
): () => void {
  let set = subscribers.get(projectId);
  if (!set) {
    set = new Set();
    subscribers.set(projectId, set);
  }
  set.add(onInvalidated);

  return () => {
    const current = subscribers.get(projectId);
    if (!current) return;
    current.delete(onInvalidated);
    if (current.size === 0) subscribers.delete(projectId);
  };
}
